/// AI Settings Dialog: API key setup, connection test, model picker, context window.
///
/// Provides guided first-use setup with consent notice, connection testing,
/// curated model picker with free/paid filter, and API key management.
/// Styled in dark zinc theme consistent with existing InovaBPM panels.

import { useState, useCallback, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import {
  X,
  Settings,
  Check,
  AlertCircle,
  Loader2,
  ExternalLink,
  Trash2,
  Search,
} from "lucide-react";
import { useAIStore } from "@/stores/aiStore";
import { testConnection } from "@/services/openrouter/client";
import {
  RECOMMENDED_MODELS,
  filterModels,
  sortModelsWithRecommended,
  isModelFree,
} from "@/services/openrouter/models";
import type { OpenRouterModel } from "@/types/ai";

// ---------------------------------------------------------------------------
// Connection test state
// ---------------------------------------------------------------------------

type ConnectionStatus = "idle" | "testing" | "success" | "error";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AISettingsDialog() {
  const settingsOpen = useAIStore((s) => s.settingsOpen);
  const closeSettings = useAIStore((s) => s.closeSettings);
  const apiKey = useAIStore((s) => s.apiKey);
  const consentGiven = useAIStore((s) => s.consentGiven);
  const defaultModel = useAIStore((s) => s.defaultModel);
  const models = useAIStore((s) => s.models);
  const contextWindowSize = useAIStore((s) => s.contextWindowSize);

  const setApiKey = useAIStore((s) => s.setApiKey);
  const clearApiKey = useAIStore((s) => s.clearApiKey);
  const giveConsent = useAIStore((s) => s.giveConsent);
  const setDefaultModel = useAIStore((s) => s.setDefaultModel);
  const setContextWindowSize = useAIStore((s) => s.setContextWindowSize);
  const loadModels = useAIStore((s) => s.loadModels);

  // Local form state
  const [keyInput, setKeyInput] = useState(apiKey ?? "");
  const [consentChecked, setConsentChecked] = useState(consentGiven);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("idle");
  const [freeOnly, setFreeOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllModels, setShowAllModels] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  // Sync key input when dialog opens
  useEffect(() => {
    if (settingsOpen) {
      setKeyInput(apiKey ?? "");
      setConsentChecked(consentGiven);
      setConnectionStatus("idle");
      setConfirmClear(false);
    }
  }, [settingsOpen, apiKey, consentGiven]);

  // Load models when dialog opens with a valid key
  useEffect(() => {
    if (settingsOpen && apiKey) {
      loadModels();
    }
  }, [settingsOpen, apiKey, loadModels]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleTestConnection = useCallback(async () => {
    if (!keyInput.trim()) return;
    setConnectionStatus("testing");
    const success = await testConnection(keyInput.trim());
    setConnectionStatus(success ? "success" : "error");
  }, [keyInput]);

  const handleSaveKey = useCallback(() => {
    if (!consentChecked || !keyInput.trim()) return;
    giveConsent();
    setApiKey(keyInput.trim());
    // Trigger model load after saving
    setTimeout(() => {
      useAIStore.getState().loadModels();
    }, 100);
  }, [consentChecked, keyInput, giveConsent, setApiKey]);

  const handleClearKey = useCallback(() => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    clearApiKey();
    setKeyInput("");
    setConnectionStatus("idle");
    setConfirmClear(false);
  }, [confirmClear, clearApiKey]);

  const handleSelectModel = useCallback(
    (modelId: string) => {
      setDefaultModel(modelId);
    },
    [setDefaultModel],
  );

  // -----------------------------------------------------------------------
  // Derived data
  // -----------------------------------------------------------------------

  const filteredModels = filterModels(models, { freeOnly, searchQuery });
  const sortedModels = sortModelsWithRecommended(filteredModels, RECOMMENDED_MODELS);

  const recommendedSet = new Set(RECOMMENDED_MODELS);
  const recommendedModels = sortedModels.filter((m) => recommendedSet.has(m.id));
  const otherModels = sortedModels.filter((m) => !recommendedSet.has(m.id));

  const canSave = consentChecked && keyInput.trim().length > 0;
  const hasKey = apiKey !== null && apiKey.length > 0;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Dialog.Root
      open={settingsOpen}
      onOpenChange={(open) => {
        if (!open) closeSettings();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl focus:outline-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-zinc-700 px-5 py-3">
            <div className="flex items-center gap-2">
              <Settings size={16} className="text-zinc-400" />
              <Dialog.Title className="text-sm font-semibold text-zinc-200">
                AI Settings
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-colors">
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <ScrollArea.Root className="max-h-[70vh]">
            <ScrollArea.Viewport className="w-full px-5 py-4">
              <div className="space-y-6">
                {/* Section 1: API Key Setup */}
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                    API Key
                  </h3>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={keyInput}
                        onChange={(e) => {
                          setKeyInput(e.target.value);
                          setConnectionStatus("idle");
                        }}
                        placeholder="sk-or-v1-..."
                        className="flex-1 rounded border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                      />
                      <button
                        onClick={handleTestConnection}
                        disabled={!keyInput.trim() || connectionStatus === "testing"}
                        className="flex items-center gap-1.5 rounded border border-zinc-700 px-3 py-2 text-xs font-medium text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {connectionStatus === "testing" && (
                          <Loader2 size={14} className="animate-spin" />
                        )}
                        {connectionStatus === "success" && (
                          <Check size={14} className="text-green-400" />
                        )}
                        {connectionStatus === "error" && (
                          <AlertCircle size={14} className="text-red-400" />
                        )}
                        {connectionStatus === "idle" && "Test"}
                        {connectionStatus === "testing" && "Testing..."}
                        {connectionStatus === "success" && "Connected"}
                        {connectionStatus === "error" && "Failed"}
                      </button>
                    </div>

                    {connectionStatus === "error" && (
                      <p className="text-xs text-red-400">
                        Connection failed. Please check your API key and try again.
                      </p>
                    )}

                    <a
                      href="https://openrouter.ai/settings/keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Get your API key at openrouter.ai
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </section>

                {/* Section 2: Consent Notice */}
                <section>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 accent-blue-500"
                    />
                    <span className="text-xs text-zinc-400 leading-relaxed">
                      I understand my API key will be stored in browser localStorage and is only
                      sent to OpenRouter. It will never be sent to any other endpoint.
                    </span>
                  </label>

                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={handleSaveKey}
                      disabled={!canSave}
                      className="rounded bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {hasKey ? "Update Key" : "Save Key"}
                    </button>
                  </div>
                </section>

                {/* Section 3: Default Model Picker */}
                {hasKey && (
                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                      Default Model
                    </h3>

                    {/* Free-only toggle + search */}
                    <div className="mb-3 flex items-center gap-3">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={freeOnly}
                          onChange={(e) => setFreeOnly(e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-800 accent-blue-500"
                        />
                        <span className="text-xs text-zinc-400">Free only</span>
                      </label>
                      <div className="flex-1 relative">
                        <Search
                          size={12}
                          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
                        />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search models..."
                          className="w-full rounded border border-zinc-700 bg-zinc-800 py-1.5 pl-7 pr-3 text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        />
                      </div>
                    </div>

                    {/* Recommended Models */}
                    {recommendedModels.length > 0 && (
                      <div className="mb-2">
                        <p className="mb-1.5 text-xs text-zinc-500 font-medium">Recommended</p>
                        <div className="space-y-1">
                          {recommendedModels.map((model) => (
                            <ModelItem
                              key={model.id}
                              model={model}
                              isSelected={model.id === defaultModel}
                              onSelect={handleSelectModel}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Models (expandable) */}
                    {otherModels.length > 0 && (
                      <div>
                        <button
                          onClick={() => setShowAllModels(!showAllModels)}
                          className="mb-1.5 text-xs text-zinc-500 font-medium hover:text-zinc-400 transition-colors"
                        >
                          {showAllModels ? "Hide" : "Show"} all models ({otherModels.length})
                        </button>
                        {showAllModels && (
                          <ScrollArea.Root className="max-h-48">
                            <ScrollArea.Viewport className="w-full">
                              <div className="space-y-1">
                                {otherModels.map((model) => (
                                  <ModelItem
                                    key={model.id}
                                    model={model}
                                    isSelected={model.id === defaultModel}
                                    onSelect={handleSelectModel}
                                  />
                                ))}
                              </div>
                            </ScrollArea.Viewport>
                            <ScrollArea.Scrollbar
                              orientation="vertical"
                              className="flex w-2 touch-none select-none rounded-full bg-zinc-800 p-0.5"
                            >
                              <ScrollArea.Thumb className="relative flex-1 rounded-full bg-zinc-600" />
                            </ScrollArea.Scrollbar>
                          </ScrollArea.Root>
                        )}
                      </div>
                    )}

                    {models.length === 0 && (
                      <p className="text-xs text-zinc-500 italic">
                        Loading models... (save your API key first)
                      </p>
                    )}
                  </section>
                )}

                {/* Section 4: Context Window */}
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                    Context Window
                  </h3>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={5}
                      max={50}
                      value={contextWindowSize}
                      onChange={(e) =>
                        setContextWindowSize(parseInt(e.target.value, 10) || DEFAULT_CONTEXT_WINDOW)
                      }
                      className="w-20 rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                    />
                    <span className="text-xs text-zinc-500">
                      messages included in AI context (5-50)
                    </span>
                  </div>
                </section>

                {/* Section 5: Clear API Key */}
                {hasKey && (
                  <section className="border-t border-zinc-800 pt-4">
                    <button
                      onClick={handleClearKey}
                      className="flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <Trash2 size={14} />
                      {confirmClear ? "Click again to confirm removal" : "Remove API Key"}
                    </button>
                  </section>
                )}
              </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              orientation="vertical"
              className="flex w-2 touch-none select-none rounded-full bg-zinc-800 p-0.5"
            >
              <ScrollArea.Thumb className="relative flex-1 rounded-full bg-zinc-600" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Model list item
// ---------------------------------------------------------------------------

const DEFAULT_CONTEXT_WINDOW = 20;

interface ModelItemProps {
  model: OpenRouterModel;
  isSelected: boolean;
  onSelect: (modelId: string) => void;
}

function ModelItem({ model, isSelected, onSelect }: ModelItemProps) {
  const free = isModelFree(model);

  return (
    <button
      onClick={() => onSelect(model.id)}
      className={[
        "flex w-full items-center justify-between rounded px-3 py-2 text-left transition-colors",
        isSelected
          ? "bg-blue-600/20 border border-blue-500/30"
          : "hover:bg-zinc-800 border border-transparent",
      ].join(" ")}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-zinc-200">{model.name}</p>
        <p className="truncate text-xs text-zinc-500">{model.id}</p>
      </div>
      <div className="ml-2 flex items-center gap-2">
        <span
          className={[
            "rounded px-1.5 py-0.5 text-xs font-medium",
            free ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400",
          ].join(" ")}
        >
          {free ? "Free" : "Paid"}
        </span>
        {isSelected && <Check size={14} className="text-blue-400" />}
      </div>
    </button>
  );
}
